import { Box, ClickAwayListener, IconButton, Popper, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/styles';
import { useEffect, useRef, useState } from 'react';

import { SelectItem } from '@/components/shared/components/AgentSelectionComponent/types/SelectItem';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { Icon } from '@/components/shared/components/Icon';
import { IconWithCircle } from '@/components/shared/components/IconWithCircle';
import { useCustomFieldStyles } from '@/components/shared/components/NewContextForms/hooks/useCustomFieldStyles';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface SpecificAutocompleteProps {
  tabIndex?: number;
  noOptionsText?: JSX.Element;
  open?: boolean;
}

interface ChannelOrAgentDropdownSelectorProps {
  handleClickItem: (item: SelectItem) => void;
  title: JSX.Element;
  subtitle: JSX.Element;
  icon: string;
  items: SelectItem[] | undefined;
  value: SelectItem | undefined;
  initialValue?: SelectItem;
  resetItem?: () => boolean;
  disabled?: boolean;
  autocompleteProps?: SpecificAutocompleteProps;
}

export const ChannelOrAgentDropdownSelector = ({
  items,
  handleClickItem,
  title,
  subtitle,
  icon,
  resetItem,
  initialValue,
  value,
  disabled = false,
  autocompleteProps,
}: ChannelOrAgentDropdownSelectorProps) => {
  const { palette, typography, customBorderRadius } = useCustomTheme();
  const { getMUIAutocompleteFormClasses } = useCustomFieldStyles({
    fieldVariant: FieldVariant.form,
    field: true,
  });

  const [anchorEl, setAnchorEl] = useState<null | undefined | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState(initialValue);
  const [openAutocomplete, setOpenAutocomplete] = useState(false);

  useEffect(() => {
    if (resetItem && resetItem() === true) setSelectedItem(undefined);
  }, [resetItem]);

  useEffect(() => {
    if (value !== selectedItem) setSelectedItem(value);
  }, [value]);

  useEffect(() => {
    if (autocompleteProps?.open) {
      setOpenAutocomplete(true);
      setAnchorEl(ref.current);
    }
  }, [autocompleteProps?.open]);

  const ref = useRef<HTMLDivElement>(null);
  const currentRefOffsetWidth = ref?.current?.offsetWidth;

  const useStyles = makeStyles({
    root: {
      '& .MuiMenu-paper': {
        width: currentRefOffsetWidth ?? '100%',
      },
      '& .MuiInputBase-root': {
        backgroundColor: 'transparent',
      },
      '& .MuiFormLabel-root': {
        fontSize: typography.subtitle2.fontSize,
      },
      '& .MuiFormControl-root': {
        margin: '0px',
        backgroundColor: palette.grey[100],
        borderRadius: `${customBorderRadius.paper}px ${customBorderRadius.paper}px 0`,
      },
      '& .MuiAutocomplete-popupIndicator': {
        display: 'none',
      },
    },
    popper: {
      '& .MuiMenu-paper': {
        width: currentRefOffsetWidth ?? '100%',
      },
      '& .MuiInputBase-root': {
        backgroundColor: 'transparent',
      },
      '& .MuiInputBase-input': {
        minHeight: '1.8rem !important',
      },
      '& .MuiFormLabel-root': {
        fontSize: typography.subtitle2.fontSize,
      },
      '& .MuiFormControl-root': {
        margin: '0px',
        backgroundColor: 'transparent',
        borderRadius: `${customBorderRadius.paper}px ${customBorderRadius.paper}px 0`,
      },
      '& .MuiAutocomplete-popupIndicator': {
        display: 'none',
      },
      margin: '0',
    },
  });
  const classes = useStyles();

  const textFieldClasses = getMUIAutocompleteFormClasses();

  const handleClick = () => {
    if (disabled) return;
    setOpenAutocomplete(!openAutocomplete);
    setAnchorEl(ref.current);
  };

  const handleClose = () => {
    setOpenAutocomplete(false);
    setAnchorEl(null);
    setTimeout(() => {}, 50);
  };

  const handleMenuSelection = (item: SelectItem | undefined) => {
    handleClose();
    if (item) {
      setSelectedItem(item);
      handleClickItem(item);
    }
  };

  return (
    <Box
      width={'100%'}
      style={{
        opacity: disabled ? '50%' : '',
        cursor: disabled ? '' : 'pointer',
      }}
    >
      <>
        <Box pb={1}>
          <Typography variant={'caption'} color={'textSecondary'}>
            {title}
          </Typography>
        </Box>
        <CustomPaper
          fill="paper"
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
          style={{
            padding: '12px',
          }}
          ref={ref}
        >
          <Box display={'flex'} alignItems={'center'}>
            <Box>
              <IconWithCircle
                width={26}
                height={26}
                bgcolor={selectedItem ? palette.secondary.main : palette.grey[400]}
                iconProps={{
                  style: {
                    verticalAlign: 'center',
                    fontSize: '20px',
                    color: palette.common.white,
                  },
                }}
              >
                {icon}
              </IconWithCircle>
            </Box>
            <Box flexGrow={1} pl={1} overflow={'hidden'}>
              <Typography className="overflow-text-ellipsis" variant={'body2'} style={{ color: palette.text.title }}>
                <strong>{selectedItem?.name ?? subtitle}</strong>
              </Typography>
            </Box>
            <Box>
              <IconButton size="small">
                <i className={'material-icons-outlined'}>expand_more</i>
              </IconButton>
            </Box>
          </Box>
        </CustomPaper>
        <ClickAwayListener
          onClickAway={() => {
            if (anchorEl) {
              handleClose();
            }
          }}
        >
          <Popper
            className={classes.root}
            id={'id'}
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            placement="bottom-start"
            style={{
              width: currentRefOffsetWidth,
            }}
            onKeyUp={(e) => {
              if (e.key === 'Escape') handleClose();
            }}
          >
            {items?.length === 0 || !items ? (
              <CustomPaper
                elevation={0}
                style={{ height: '100%', padding: 0 }}
                onClick={() => handleMenuSelection(undefined)}
              >
                <Typography variant={'body2'} color={'textSecondary'}>
                  <Text tId={'NO_AGENTS_AVAILABLE'} />
                </Typography>
              </CustomPaper>
            ) : (
              <CustomPaper
                fill="paper"
                style={{ padding: 0, borderRadius: `${customBorderRadius.paper}px ${customBorderRadius.paper}px 0 0` }}
              >
                <Autocomplete
                  classes={textFieldClasses}
                  className={classes.popper}
                  options={items}
                  openOnFocus
                  open={true}
                  getOptionLabel={(option) => option.name}
                  onChange={(_, value) => {
                    if (value) handleMenuSelection(value);
                  }}
                  renderInput={(params) => {
                    return <TextField autoFocus {...params} label={<Text tId={'SEARCH_BY_NAME'} />} variant="filled" />;
                  }}
                  renderOption={(item: SelectItem) => (
                    <Box width={'100%'} display={'inline-flex'} justifyContent={'space-between'} alignItems={'center'}>
                      <Typography variant={'body2'}>{item.name}</Typography>
                      <Icon color={palette.text.primary} size={20}>
                        {item.icon}
                      </Icon>
                    </Box>
                  )}
                  PaperComponent={({ children }) => (
                    <CustomPaper
                      fill="paper"
                      style={{
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        maxHeight: 200,
                        overflow: 'auto',
                        padding: 0,
                      }}
                    >
                      {children}
                    </CustomPaper>
                  )}
                  {...autocompleteProps}
                />
              </CustomPaper>
            )}
          </Popper>
        </ClickAwayListener>
      </>
    </Box>
  );
};
